<template>
  <div class="view-container">
    <div class="page-card">
      <div class="page-title"><i class="fas fa-user-circle"></i> 个人信息</div>
      <div class="info-layout">
        <div class="avatar-section">
          <div class="avatar-wrapper" @click="triggerAvatar">
            <img v-if="form.avatar" :src="form.avatar" class="avatar-img" alt="头像">
            <div v-else class="avatar-placeholder"><i class="fas fa-user"></i></div>
            <div class="avatar-overlay"><i class="fas fa-camera"></i><span>更换头像</span></div>
            <input ref="avatarInputRef" type="file" accept="image/*" style="display:none" @change="handleAvatarChange">
          </div>
          <span class="avatar-tip">点击更换头像</span>
        </div>

        <div class="form-section">
          <div class="form-row">
            <div class="form-group">
              <label>账号</label>
              <input :value="form.account" type="text" class="form-control" disabled>
            </div>
            <div class="form-group">
              <label>姓名</label>
              <input v-model="form.name" type="text" class="form-control" placeholder="请输入姓名">
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>性别</label>
              <select v-model="form.gender" class="form-control">
                <option value="男">男</option>
                <option value="女">女</option>
              </select>
            </div>
            <div class="form-group">
              <label>工号</label>
              <input :value="form.employeeNo" type="text" class="form-control" disabled>
            </div>
            <div class="form-group">
              <label>用户类型</label>
              <input :value="form.userType" type="text" class="form-control" disabled>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>手机</label>
              <input v-model="form.phone" type="text" class="form-control" placeholder="请输入手机号">
            </div>
            <div class="form-group">
              <label>邮箱</label>
              <input v-model="form.email" type="text" class="form-control" placeholder="请输入邮箱">
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>职位</label>
              <input :value="form.position" type="text" class="form-control" disabled>
            </div>
            <div class="form-group">
              <label>直接领导</label>
              <input :value="form.leader" type="text" class="form-control" disabled>
            </div>
          </div>

          <div class="form-group full-width">
            <label>电子签名</label>
            <div class="signature-upload" @click="triggerSignature">
              <img v-if="form.signature" :src="form.signature" class="signature-img" alt="电子签名">
              <div v-else class="signature-placeholder">
                <i class="fas fa-signature"></i>
                <span>点击上传电子签名（建议透明背景PNG）</span>
              </div>
              <input ref="signatureInputRef" type="file" accept="image/*" style="display:none" @change="handleSignatureChange">
            </div>
          </div>

          <div class="form-actions">
            <span v-if="savedTip" class="save-tip"><i class="fas fa-check-circle"></i> {{ savedTip }}</span>
            <button @click="saveProfileHandle" class="btn btn-primary" :disabled="saving">
              <i class="fas fa-save"></i> {{ saving ? '保存中...' : '保存修改' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { getProfile } from '../api/auth'
import { saveProfile, uploadAvatar, uploadSignature } from '../api/user'

const apiBaseUrl = 'http://localhost:5260'

const avatarInputRef = ref(null)
const signatureInputRef = ref(null)
const saving = ref(false)
const savedTip = ref('')
const tempAvatarPath = ref('')
const tempSignaturePath = ref('')

const form = reactive({
  account: '',
  name: '',
  gender: '男',
  employeeNo: '',
  userType: '普通用户',
  phone: '',
  email: '',
  position: '',
  leader: '',
  avatar: '',
  signature: ''
})

const triggerAvatar = () => avatarInputRef.value?.click()
const triggerSignature = () => signatureInputRef.value?.click()

const loadProfile = async () => {
  try {
    const res = await getProfile()
    const data = res.data || res.Data || {}
    form.account = data.account || data.Account || ''
    form.name = data.displayName || data.DisplayName || ''
    form.gender = (data.sex ?? data.Sex) === 1 ? '男' : '女'
    form.employeeNo = data.hrid || data.HRID || ''
    form.userType = data.userType || data.UserType || '普通用户'
    form.phone = data.mb || data.Mb || data.tel || data.Tel || ''
    form.email = data.email || data.Email || ''
    form.position = data.post || data.Post || ''
    form.leader = data.superManageName || data.SuperManageName || ''
    form.avatar = (data.para1 || data.Para1) ? apiBaseUrl + (data.para1 || data.Para1) : ''
    form.signature = (data.para2 || data.Para2) ? apiBaseUrl + (data.para2 || data.Para2) : ''
  } catch (e) {
    console.error('加载个人信息失败', e)
  }
}

const handleAvatarChange = async (e) => {
  const file = e.target.files[0]
  if (!file) return
  const fd = new FormData()
  fd.append('file', file)
  try {
    const res = await uploadAvatar(fd)
    if (res.resultType || res.ResultType) {
      const path = res.data || res.Data || ''
      tempAvatarPath.value = path
      form.avatar = apiBaseUrl + path + '?t=' + Date.now()
      $message.success('头像已选择（点保存后生效）')
    }
  } catch (err) { $message.error('上传失败') }
}

const handleSignatureChange = async (e) => {
  const file = e.target.files[0]
  if (!file) return
  const fd = new FormData()
  fd.append('file', file)
  try {
    const res = await uploadSignature(fd)
    if (res.resultType || res.ResultType) {
      const path = res.data || res.Data || ''
      tempSignaturePath.value = path
      form.signature = apiBaseUrl + path + '?t=' + Date.now()
      $message.success('签名已选择（点保存后生效）')
    }
  } catch (err) { $message.error('上传失败') }
}

const saveProfileHandle = async () => {
  if (!form.name.trim()) { $message.warning('请输入姓名'); return }
  saving.value = true
  try {
    const res = await saveProfile({
      name: form.name,
      gender: form.gender,
      phone: form.phone,
      email: form.email,
      avatar: tempAvatarPath.value || null,
      signature: tempSignaturePath.value || null
    })
    if (res.resultType || res.ResultType) {
      tempAvatarPath.value = ''
      tempSignaturePath.value = ''
      $message.success('保存成功')
      loadProfile() // 重新加载以获取正式路径
    }
  } catch (e) { $message.error('保存失败') }
  finally { saving.value = false }
}

onMounted(() => {
  loadProfile()
})
</script>

<style scoped>
.view-container {
  padding: 16px;
  background: #f5f7fa;
  min-height: calc(94vh - 110px);
}
.page-card {
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e8ecf1;
  overflow: hidden;
  max-width: 960px;
  margin: 0 auto;
}
.page-title {
  padding: 16px 24px;
  font-size: 15px;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid #e8ecf1;
  background: #fafbfc;
}
.page-title i { color: #1ab394; }

.info-layout { display: flex; gap: 32px; padding: 24px; }
.avatar-section { display: flex; flex-direction: column; align-items: center; gap: 10px; flex-shrink: 0; }
.avatar-wrapper {
  width: 100px; height: 100px; border-radius: 50%; overflow: hidden; cursor: pointer;
  position: relative; border: 3px solid #e8ecf1; transition: border-color 0.2s;
}
.avatar-wrapper:hover { border-color: #1ab394; }
.avatar-img { width: 100%; height: 100%; object-fit: cover; }
.avatar-placeholder { width: 100%; height: 100%; background: linear-gradient(135deg, #d1fae5, #a7f3d0); display: flex; align-items: center; justify-content: center; }
.avatar-placeholder i { font-size: 36px; color: #059669; }
.avatar-overlay {
  position: absolute; inset: 0; background: rgba(0,0,0,.45); display: flex; flex-direction: column;
  align-items: center; justify-content: center; gap: 2px; opacity: 0; transition: opacity .2s;
  color: #fff; font-size: 10px;
}
.avatar-wrapper:hover .avatar-overlay { opacity: 1; }
.avatar-tip { font-size: 11px; color: #9ca3af; }
.form-section { flex: 1; min-width: 0; }
.form-row { display: flex; gap: 16px; margin-bottom: 14px; }
.form-row .form-group { flex: 1; }
.form-group { margin-bottom: 14px; }
.form-group.full-width { margin-bottom: 16px; }
.form-group label { display: block; font-size: 12px; font-weight: 500; color: #374151; margin-bottom: 5px; }
.form-control {
  width: 100%; padding: 8px 10px; border: 1px solid #d1d5db; border-radius: 5px; font-size: 13px;
  outline: none; background: #fff; box-sizing: border-box;
}
.form-control:focus { border-color: #1ab394; box-shadow: 0 0 0 2px rgba(26,179,148,.1); }
.form-control:disabled { background: #f9fafb; color: #6b7280; cursor: not-allowed; }
.form-actions { display: flex; align-items: center; justify-content: flex-end; gap: 12px; padding-top: 14px; border-top: 1px solid #f3f4f6; }
.save-tip { display: flex; align-items: center; gap: 4px; font-size: 12px; color: #1ab394; }

.signature-upload { position: relative; width: 100%; height: 90px; border: 2px dashed #d1d5db; border-radius: 6px; cursor: pointer; overflow: hidden; transition: border-color .2s; }
.signature-upload:hover { border-color: #1ab394; }
.signature-img { width: 100%; height: 100%; object-fit: contain; background: #fff; }
.signature-placeholder { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; color: #9ca3af; gap: 4px; font-size: 12px; }
.signature-placeholder i { font-size: 24px; color: #d1d5db; }

.btn { padding: 8px 18px; border-radius: 5px; font-size: 13px; cursor: pointer; display: inline-flex; align-items: center; gap: 5px; border: none; }
.btn:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-primary { background: #1ab394; color: #fff; }
.btn-primary:hover:not(:disabled) { background: #18a689; }
</style>
